import { useMemo } from 'react';

const INITIAL_NAME = /^\p{L}\.\s/u;
const PLACEHOLDER_RE = /\{\w+\}/;
const SCORE_COMMENT_RE =
  /(?:Final del partido|Final (?:primera|segunda) parte|Empieza segunda parte),?\s*(.+?)\s+(\d+),\s*(.+?)\s+(\d+)\.?\s*$/i;

const teamDataName = (td, teams) => {
  if (!td) return null;
  const raw = td.teamName ?? td.name;
  return (raw && teams?.[raw]?.label) || td.teamShortName || td.shortName || raw || null;
};

// Nombre tal como lo muestra el comentario: matchName salvo que sea "J. Perea"
export const getDisplayName = (p) => {
  if (!p) return null;
  if (p.matchName && !INITIAL_NAME.test(p.matchName)) return p.matchName;
  const name = `${p.shortFirstName || p.firstName || ''} ${p.shortLastName || p.lastName || ''}`.trim();
  return name || p.matchName || null;
};

const teamFromComment = (comment = '') =>
  comment.match(/\(([^)]+)\)/)?.[1] ||          // "Haris Tabakovic (Salzburgo) ..."
  comment.match(/^Cambio en ([^,]+),/)?.[1] ||  // "Cambio en Salzburgo, entra ..."
  null;

// Para el padre: local y visitante en español
export const getMatchTeams = (title = '', body = []) => {
  const [home, away] = title.split(/\s+vs\.?\s+/i).map((s) => s.trim());
  if (home && away) return { home, away };
  const opta = body.find((i) => i.type === 'mams_opta')?.value?.teams;
  return opta ? { home: opta.home?.name, away: opta.away?.name } : null;
};

const buildEventVars = (value, match, teams) => {
  const { comment = '', playerData1: p1, playerData2: p2 } = value;
  const t = (name) => (name ? teams?.[name]?.label ?? name : null);

  const rawTeam = p1?.teamName;
  const team =
    (rawTeam && teams?.[rawTeam]?.label) ||
    teamFromComment(comment) ||
    p1?.teamShortName ||
    rawTeam ||
    null;

  const score = comment.match(SCORE_COMMENT_RE);

  // teamData1 = local, teamData2 = visitante; si faltan, cae a match del padre
  const d1 = teamDataName(value.teamData1, teams);
  const d2 = teamDataName(value.teamData2, teams);
  const pair = d1 && d2
    ? { home: d1, away: d2 }
    : { home: t(match?.home), away: t(match?.away) };

  return {
    team,
    player: getDisplayName(p1),
    playerIn: getDisplayName(p1),
    playerOut: getDisplayName(p2),
    homeTeam: score ? score[1] : pair.home,
    homeScore: score ? score[2] : null,
    awayTeam: score ? score[3] : pair.away,
    awayScore: score ? score[4] : null,
  };
};

// Devuelve null si falta algún placeholder
const fillTemplate = (template, vars) => {
  let complete = true;
  const text = template.replace(/\{(\w+)\}/g, (_, key) => {
    const v = vars[key];
    if (v === undefined || v === null || v === '') {
      complete = false;
      return '';
    }
    return v;
  });
  return complete ? text : null;
};

/**
 * Transforma la estructura del CMS (array de objetos con extras) al formato esperado por el hook
 * @param {Array} menu - Array del CMS con formato [{name, extras}, ...]
 * @returns {Object|null} - Estructura transformada o null si no hay datos
 */
export const transformCMSStructure = (menu) => {
  if (!menu || !Array.isArray(menu) || menu.length === 0) {
    console.log('transformCMSStructure - returning null: menu is not array or is empty');
    return null;
  }

  // Buscar contexto default, si no existe usar el primer elemento
  const defaultData = menu.find(item =>
    item.name?.toLowerCase() === 'default'
  ) || menu[0];

  const extras = defaultData?.extras?.extras || {};

  // Construir estructura esperada
  return {
    contexts: {
      default: extras
    },
    events: extras?.events || {},
    glossary: extras?.glossary || {},
    teams: extras?.teams || {},
    mappings: extras?.mappings || {}
  };
};

/**
 * Hook para transformar y usar traducciones desde el CMS
 * @param {Array} menu - Array del CMS con formato [{name, extras}, ...]
 * @returns {Object} { traduccionesOpta, resolveEvent, processNarrative, resolveTeamName }
 */
export const useOptaTranslations = (menu) => {
  // Transformar estructura del CMS al formato esperado
  const traduccionesOpta = useMemo(() => {
    return transformCMSStructure(menu);
  }, [menu]);
  // Usar el hook existente con la estructura transformada
  const translationMethods = useTranslationResolver(traduccionesOpta);
  return {
    traduccionesOpta,
    ...translationMethods
  };
};

/**
 * Hook para resolver traducciones de eventos y procesar textos narrativos
 *
 * @param {Object} traduccionesOpta - JSON completo de traducciones
 * @returns {Object} { resolveEvent, processNarrative }
 */
export const useTranslationResolver = (traduccionesOpta) => {

  /**
   * Resuelve la traducción de un evento
   */
  const resolveEvent = useMemo(() => {
    return (eventType, period = null, value = null, match = null) => {
      if (!traduccionesOpta?.contexts) {
        console.warn('[Translation] JSON de traducciones no disponible');
        return { label: eventType, icon: '', heading: false };
      }

      const contexts = traduccionesOpta.contexts;
      const mappings = traduccionesOpta.mappings?.optaEventTypes || {};

      // 1. Normalizar evento usando mappings
      let normalized = eventType;
      const mapping = mappings[eventType];

      if (mapping) {
        if (typeof mapping === 'string') {
          normalized = mapping;
        } else if (mapping.event) {
          // Caso especial: "end 1" -> { event: "end", period: "1" }
          normalized = mapping.event;
          period = mapping.period;
        }
      } else {
        // Si no hay mapping, reemplazar espacios por guion bajo
        normalized = eventType?.replace(/ /g, '_');
      }

      // 2. Buscar en contexto default
      let translation = contexts.default?.events?.[normalized];

      // 3. Si tiene períodos, buscar período específico
      if (translation?.periods && period) {
        translation = translation.periods[String(period)];
      }

      // 4. Fallback final
      if (!translation) {
        return { label: eventType, icon: contexts.default?.fallbackIcon || '', heading: false };
      }

      let label = translation.label || eventType;

      let heading = Boolean(translation.heading);

      if (PLACEHOLDER_RE.test(label)) {
        const filled = value
          ? fillTemplate(label, buildEventVars(value, match, contexts.default?.teams))
          : null;

        if (filled === null) {
          heading = false; // no publicar un h2 incompleto
          label = translation.fallbackLabel || eventType;
        } else {
          label = filled;
        }
      }

      return {
        label,
        icon: translation.icon || contexts.default?.fallbackIcon || '',
        heading,
      };
    };
  }, [traduccionesOpta]);

  /**
   * Procesa texto narrativo aplicando el glosario
   */
  const processNarrative = useMemo(() => {
    return (text) => {
      if (!traduccionesOpta?.contexts || !text) {
        return text;
      }

      const glossary = traduccionesOpta.contexts.default?.glossary;

      if (!glossary) {
        console.warn('[Translation] No hay glosario en contexto default');
        return text;
      }

      let processedText = text;

      // 1. Aplicar patterns (regex) - Orden de mayor a menor complejidad
      if (glossary.patterns && Array.isArray(glossary.patterns)) {
        glossary.patterns.forEach(pattern => {
          try {
            const regex = new RegExp(pattern.regex, 'gi');
            processedText = processedText.replace(regex, pattern.replace);
          } catch (error) {
            console.error(`[Translation] Error en pattern regex: ${pattern.regex}`, error);
          }
        });
      }

      // 2. Aplicar phrases (frases completas) antes que términos individuales
      if (glossary.phrases && typeof glossary.phrases === 'object') {
        Object.entries(glossary.phrases).forEach(([original, replacement]) => {
          // Escape de caracteres especiales en la búsqueda
          const escaped = original.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          const regex = new RegExp(escaped, 'gi');
          processedText = processedText.replace(regex, replacement);
        });
      }

      // 3. Aplicar terms (palabras individuales) - Con word boundaries
      if (glossary.terms && typeof glossary.terms === 'object') {
        Object.entries(glossary.terms).forEach(([original, replacement]) => {
          // Escape y usar word boundaries para evitar reemplazos parciales
          const escaped = original.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          const regex = new RegExp(`\\b${escaped}\\b`, 'g');
          processedText = processedText.replace(regex, replacement);
        });
      }

      return processedText;
    };
  }, [traduccionesOpta]);

  /**
   * Resuelve el nombre traducido de un equipo/país
   */
  const resolveTeamName = useMemo(() => {
    return (teamName, useShortLabel = true) => {
      if (!traduccionesOpta?.contexts || !teamName) {
        return teamName;
      }

      const teams = traduccionesOpta.contexts.default?.teams;

      if (!teams) {
        console.warn('[Translation] No hay traducciones de equipos en contexto default');
        return teamName;
      }

      // Buscar el equipo en el contexto
      const teamTranslation = teams[teamName];

      if (teamTranslation) {
        return useShortLabel && teamTranslation.shortLabel
          ? teamTranslation.shortLabel
          : teamTranslation.label;
      }

      // Fallback: retornar nombre original
      return teamName;
    };
  }, [traduccionesOpta]);

  return {
    resolveEvent,
    processNarrative,
    resolveTeamName
  };
};

/**
 * Función standalone para usar fuera de React
 */
export function resolveEventTranslation(traduccionesOpta, eventType, period) {
  const contexts = traduccionesOpta?.contexts || {};
  const mappings = traduccionesOpta?.mappings?.optaEventTypes || {};

  // Normalizar
  let normalized = eventType;
  const mapping = mappings[eventType];

  if (mapping) {
    if (typeof mapping === 'string') {
      normalized = mapping;
    } else if (mapping.event) {
      normalized = mapping.event;
      period = mapping.period;
    }
  } else {
    normalized = eventType.replace(/ /g, '_');
  }

  // Buscar en contexto default
  let translation = contexts.default?.events?.[normalized];

  if (translation?.periods && period) {
    translation = translation.periods[String(period)];
  }

  return translation || {
    label: eventType,
    icon: contexts.default?.fallbackIcon || ''
  };
}

/**
 * Función standalone para procesar narrativas
 */
export function processNarrativeText(traduccionesOpta, text) {
  const glossary = traduccionesOpta?.contexts?.default?.glossary;

  if (!glossary || !text) return text;

  let processedText = text;

  // Patterns
  if (glossary.patterns) {
    glossary.patterns.forEach(pattern => {
      try {
        const regex = new RegExp(pattern.regex, 'gi');
        processedText = processedText.replace(regex, pattern.replace);
      } catch (error) {
        console.error(`Error en pattern: ${pattern.regex}`, error);
      }
    });
  }

  // Phrases
  if (glossary.phrases) {
    Object.entries(glossary.phrases).forEach(([original, replacement]) => {
      const escaped = original.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(escaped, 'gi');
      processedText = processedText.replace(regex, replacement);
    });
  }

  // Terms
  if (glossary.terms) {
    Object.entries(glossary.terms).forEach(([original, replacement]) => {
      const escaped = original.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(`\\b${escaped}\\b`, 'g');
      processedText = processedText.replace(regex, replacement);
    });
  }

  return processedText;
}
